import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    CardActions,
    Stack,
    Divider,
    Snackbar,
    Alert,
    AlertColor,
    Menu,
    MenuItem,
    IconButton
} from '@mui/material';
import { Download as DownloadIcon, Cancel as CancelIcon, CheckCircle as CheckCircleIcon, Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { SubscriptionService } from '../../services/modules/subscription';
import { getSubscriptionPlans } from '../../constants/subscription';
import type { SubscriptionPlan } from '../../services/types/subscription';
import { stripePromise } from '../../services/modules/stripe';

// Initialize Stripe outside component to avoid recreating the instance

interface QuizAttempt {
    attempt_id: number;
    quiz_title: string;
    theme_title: string;
    score: number;
    pass_mark: number;
    passed: boolean;
    completed_at: string;
    certificate_url?: string;
    failed_attempts: number;
    max_attempts: number;
    question_paper_id: number;
    is_free?: boolean;
}

// Using imported SubscriptionPlan type from '../../services/types/subscription'

interface SubscriptionPlansDialogProps {
    open: boolean;
    onClose: () => void;
}

const SubscriptionPlansDialog: React.FC<SubscriptionPlansDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const handleSubscribe = async (plan: SubscriptionPlan) => {
        try {
            if (!plan.available || loadingPlanId !== null) {
                return;
            }

            if (!user?.token) {
                window.location.href = '/login';
                return;
            }

            setLoadingPlanId(plan.id);

            // Get Stripe.js instance
            const stripe = await stripePromise;
            if (!stripe) {
                console.error('Failed to load Stripe');
                setSnackbarMessage('Failed to load payment system');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                setLoadingPlanId(null);
                return;
            }

            const response = await SubscriptionService.createCheckoutSession(
                user.token,
                `${process.env.REACT_APP_FRONTEND_URL}/student/attempts`,  // Match the route defined in App.tsx
                plan.id
            );

            // Redirect to Stripe Checkout using the URL
            window.location.href = response.url;
        } catch (err) {
            console.error('Failed to initiate subscription:', err);
            setSnackbarMessage('Failed to initiate subscription');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setLoadingPlanId(null);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ textAlign: 'center', py: 3 }}>
                <Box>
                    <Typography variant="h4" component="div" fontWeight="bold">
                        Choose Your Plan
                    </Typography>
                    <Typography variant="subtitle1" component="div" color="text.secondary" sx={{ mt: 1 }}>
                        Select the plan that best fits your needs
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ py: 2 }}>
                    {getSubscriptionPlans(t).map((plan: SubscriptionPlan) => (
                        <Grid item xs={12} md={4} key={plan.id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    opacity: plan.available ? 1 : 0.7
                                }}
                            >
                                {!plan.available && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%) rotate(-30deg)',
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            py: 1,
                                            px: 4,
                                            zIndex: 1
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ fontStyle: 'italic' }}>{t('quiz.comingSoon')}</Typography>
                                    </Box>
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
                                        {plan.name}
                                    </Typography>
                                    <Box sx={{ textAlign: 'center', my: 2, minHeight: '100px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        {plan.available ? (
                                            <>
                                                <Typography variant="h4" color="primary" gutterBottom>
                                                    MYR {plan.monthlyPrice}
                                                    <Typography variant="subtitle1" component="span" color="text.secondary">
                                                        /month
                                                    </Typography>
                                                </Typography>
                                                {plan.yearlyPrice && (
                                                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                                                        MYR {plan.yearlyPrice} billed annually
                                                    </Typography>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Pricing will be announced soon
                                            </Typography>
                                        )}
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Stack spacing={2}>
                                        {plan.features.map((feature: string, index: number) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                                                <Typography>{feature}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </CardContent>
                                <CardActions sx={{ p: 2, pt: 0 }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleSubscribe(plan)}
                                        disabled={!plan.available || loadingPlanId !== null}
                                        sx={{
                                            py: 1.5,
                                            '&.Mui-disabled': {
                                                backgroundColor: loadingPlanId === plan.id ? 'primary.main' : 'action.disabledBackground',
                                                color: loadingPlanId === plan.id ? 'white' : 'action.disabled'
                                            }
                                        }}
                                    >
                                        {loadingPlanId === plan.id ? (
                                            <CircularProgress size={24} color="inherit" />
                                        ) : (
                                            plan.available ? t('subscription.subscribe') : t('quiz.comingSoon')
                                        )}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined" 
                    color="primary"
                    disabled={loadingPlanId !== null}
                    sx={{ px: 4 }}
                >
                    Cancel
                </Button>
            </DialogActions>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity={snackbarSeverity}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

const QuizAttempts: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [maxFreeCertificates, setMaxFreeCertificates] = useState<number>(1);
    const [emailEnabled, setEmailEnabled] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { user, refreshUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Function to fetch attempts
    const fetchAttempts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/student/quiz-attempts', {
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                }
            });

            if (response.status !== 200) {
                if (response.status === 401) {
                    refreshUser();
                    navigate('/login');
                    return;
                }
                throw new Error(t('quiz.fetchError'));
            }

            // Sort attempts by completed_at in descending order (newest first)
            const sortedAttempts = response.data.sort((a: QuizAttempt, b: QuizAttempt) => 
                new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
            );
            
            setAttempts(sortedAttempts);
        } catch (err) {
            console.error('Error fetching attempts:', err);
            setError(t('quiz.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [user?.token, refreshUser, navigate]);

    // Function to check if an attempt is downloadable
    const isAttemptDownloadable = useCallback((attempt: QuizAttempt) => {
        if (user?.is_subscribed) return true;  // Subscribed users can download all certificates
        if (!attempt.passed) return false;  // Must pass to download
        
        // If the paper is marked as free, it's always downloadable regardless of subscription status
        if (attempt.is_free) return true;
        
        // For unsubscribed users, get all passed attempts sorted by completion date (newest first)
        const passedAttempts = attempts
            .filter(a => a.passed && !a.is_free) // Only count non-free papers against the limit
            .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime());
        
        // Find position of this attempt in the sorted list (0 is most recent)
        const attemptIndex = passedAttempts.findIndex(a => a.attempt_id === attempt.attempt_id);
        
        // If this attempt is not in the filtered list (because it's free), it won't be found
        if (attemptIndex === -1) return true;
        
        // If total passed attempts exceeds limit, lock the most recent ones
        if (passedAttempts.length > maxFreeCertificates) {
            // Lock if this is one of the most recent attempts
            // e.g., if limit is 5 and we have 7 attempts:
            // - index 0,1 (2 most recent) will return false (locked)
            // - index 2,3,4,5,6 (5 oldest) will return true (downloadable)
            return attemptIndex >= (passedAttempts.length - maxFreeCertificates);
        }
        
        // If we haven't exceeded the limit, all papers are downloadable
        return true;
    }, [user?.is_subscribed, attempts, maxFreeCertificates]);

    // Fetch settings
    const fetchSettings = useCallback(async () => {
        if (!user?.token) return;
        
        try {
            const [maxCertsResponse, emailResponse] = await Promise.all([
                api.get('/admin/settings/max-free-certificates', {
                    headers: {
                        'Authorization': `Bearer ${user.token}`
                    }
                }),
                api.get('/admin/settings/email-certificate-enabled', {
                    headers: {
                        'Authorization': `Bearer ${user.token}`
                    }
                })
            ]);
            setMaxFreeCertificates(parseInt(maxCertsResponse.data.value));
            setEmailEnabled(emailResponse.data.value === 'true');
        } catch (err) {
            console.error('Error fetching settings:', err);
            // Use default values
            setMaxFreeCertificates(1);
            setEmailEnabled(false);
        }
    }, [user?.token]);

    const handleDownloadCertificate = async (attemptId: number) => {
        const attemptIndex = attempts.findIndex(a => a.attempt_id === attemptId);
        
        if (!isAttemptDownloadable(attempts[attemptIndex])) {
            setErrorMessage(t('quiz.subscriptionError'));
            setShowSubscriptionDialog(true);
            return;
        }

        try {
            setProgressOpen(true);
            setProgressMessage(emailEnabled ? t('quiz.emailSuccess') : t('quiz.downloadSuccess'));
            const response = await api.get(`/student/quiz-attempts/${attemptId}/certificate`, {
                params: {
                    lang: i18n.language // Send current language
                },
                headers: {
                    'Authorization': `Bearer ${user?.token}`
                },
                responseType: 'blob'
            });

            // Hide spinner as soon as download starts
            setProgressOpen(false);

            // Create a blob URL and trigger download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `certificate_${attemptId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error('Error downloading certificate:', err);
            if (err.response?.status === 403) {
                setErrorMessage(err.response.data?.message || 'You have reached your free certificate download limit. Please subscribe to download more certificates.');
                setShowSubscriptionDialog(true);
            } else {
                setError(t('quiz.downloadError'));
            }
        }
    };

    const handleSubscribe = async () => {
        try {
            if (!user?.token) {
                console.error(t('quiz.authError'));
                return;
            }

            const response = await SubscriptionService.createCheckoutSession(
                user.token,
                `${process.env.REACT_APP_FRONTEND_URL}/student/attempts`,  // Match the route defined in App.tsx
                1  // Default plan ID
            );

            // Redirect to Stripe Checkout using the URL
            if (response?.url) {
                window.location.href = response.url;
            } else {
                throw new Error(t('quiz.invalidResponseError'));
            }
        } catch (err) {
            console.error('Failed to initiate subscription:', err);
            setError(t('quiz.subscriptionInitError'));
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const sessionId = searchParams.get('session_id');

        if (sessionId && user?.token) {
            // Verify the session only once
            SubscriptionService.verifySession(sessionId, user.token)
                .then(async () => {
                    // Add a small delay to ensure webhook is processed
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    await refreshUser();  // Refresh user data to update subscription status
                    navigate('/student/attempts', { replace: true });
                })
                .catch((error) => {
                    console.error('Failed to verify session:', error);
                    setError(t('quiz.subscriptionVerifyError'));
                });
        }
    }, [user?.token, location.search, navigate, refreshUser]);

    useEffect(() => {
        fetchAttempts();
        fetchSettings();
    }, [fetchAttempts, fetchSettings]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        
        if (sessionId) {
            const verifySubscription = async () => {
                try {
                    await api.get(`/subscription/verify/${sessionId}`, {
                        headers: {
                            'Authorization': `Bearer ${user?.token}`
                        }
                    });
                    
                    // Clear the session_id from URL
                    navigate('/student/attempts', { replace: true });
                    
                    // Update user profile to get new subscription status
                    await refreshUser();
                    
                    // Immediately refetch attempts to update UI
                    await fetchAttempts();
                } catch (err) {
                    console.error('Error verifying subscription:', err);
                }
            };
            
            verifySubscription();
        }
    }, [location.search, user?.token, navigate, refreshUser, fetchAttempts]);

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewAttempt = (attemptId: number) => {
        navigate(`/student/attempts/${attemptId}`);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mt: 4, mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h4" component="div" gutterBottom>
                            {t('quiz.title')}
                        </Typography>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}

                    {attempts.length === 0 ? (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="body1" component="div" color="text.secondary">
                                    {t('quiz.noAttempts')}
                                </Typography>
                            </Paper>
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Grid container spacing={3}>
                                {attempts.map((attempt) => (
                                    <Grid item xs={12} key={attempt.attempt_id}>
                                        <Card>
                                            <CardContent>
                                                <Grid container>
                                                    <Grid item xs={4}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            alignItems: 'flex-start',
                                                            justifyContent: 'center',
                                                            height: '100%'
                                                        }}>
                                                            <Typography variant="h6" sx={{ mb: 1 }}>
                                                                {attempt.quiz_title}
                                                            </Typography>
                                                            <Typography color="textSecondary">
                                                                {t('quiz.themeLabel')}: {attempt.theme_title}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            flexDirection: 'column', 
                                                            justifyContent: 'center',
                                                            height: '100%'
                                                        }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {t('quiz.completedLabel')}: {new Date(attempt.completed_at).toLocaleString('en-US', {
                                                                    day: '2-digit',
                                                                    month: 'short',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                    hour12: true
                                                                })}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {t('quiz.attemptsLabel')}: {attempt.failed_attempts + (attempt.passed ? 1 : 0)} of {attempt.max_attempts}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs={5}>
                                                        <Grid 
                                                            container 
                                                            alignItems="center" 
                                                            sx={{ height: '100%' }}
                                                        >
                                                            <Grid item xs={3}>
                                                                <Box sx={{ 
                                                                    width: '120px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    height: '100%'
                                                                }}>
                                                                    <Typography 
                                                                        variant="subtitle1" 
                                                                    >
                                                                        {t('quiz.scoreLabel')}: {attempt.score}%
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Box sx={{ 
                                                                    width: '100px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    height: '100%'
                                                                }}>
                                                                    <Chip
                                                                        label={attempt.passed ? t('quiz.passedStatus') : t('quiz.failedStatus')}
                                                                        color={attempt.passed ? "success" : "error"}
                                                                        icon={attempt.passed ? <CheckCircleIcon /> : <CancelIcon />}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ 
                                                                            width: '100%',
                                                                            '& .MuiChip-icon': {
                                                                                color: attempt.passed ? 'success.main' : 'error.main'
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Grid>
                                                            <Grid item xs={6}>
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    justifyContent: 'flex-end',
                                                                    alignItems: 'center',
                                                                    height: '100%',
                                                                    pr: 2
                                                                }}>
                                                                    {attempt.certificate_url && attempt.passed && (
                                                                        <Button
                                                                            startIcon={isAttemptDownloadable(attempt) ? <DownloadIcon /> : <LockIcon />}
                                                                            onClick={() => isAttemptDownloadable(attempt) ? handleDownloadCertificate(attempt.attempt_id) : setShowSubscriptionDialog(true)}
                                                                            variant="contained"
                                                                            color={isAttemptDownloadable(attempt) ? "primary" : "secondary"}
                                                                            size="small"
                                                                            sx={{ 
                                                                                width: '120px',
                                                                                textTransform: 'none'
                                                                            }}
                                                                        >
                                                                            {isAttemptDownloadable(attempt) ? t('quiz.certificateButton') : t('quiz.unlockButton')}
                                                                        </Button>
                                                                    )}
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Box>
            <Dialog
                open={!!error}
                onClose={() => setError(null)}
                PaperProps={{
                    sx: {
                        width: '500px',
                        height: '180px',
                        backgroundColor: 'white',
                        borderRadius: 1,
                        p: 3,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                    }
                }}
            >
                <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                        mb: 2,
                        color: 'primary.main',
                        fontWeight: 600
                    }}
                >
                    {t('quiz.errorTitle')}
                </Typography>
                <Typography sx={{ color: 'text.primary' }}>
                    {error}
                </Typography>
            </Dialog>

            <SubscriptionPlansDialog
                open={showSubscriptionDialog}
                onClose={() => setShowSubscriptionDialog(false)}
            />
            <Dialog open={progressOpen} PaperProps={{ sx: { pointerEvents: 'auto' } }} disableEscapeKeyDown>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                    <Box sx={{ mb: 2 }}>
                        <CircularProgress size={48} />
                    </Box>
                    <Typography variant="body1">{progressMessage}</Typography>
                </DialogContent>
            </Dialog>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
                <MenuItem onClick={handleMenuClose}>My account</MenuItem>
            </Menu>
        </Container>
    );
};

export default QuizAttempts;
